/**
 * aiSessionClient.ts
 * AI 会话的前端持久化客户端。
 * 将会话数据持久化到服务端 PostgreSQL（ai_chat_sessions 表），
 * 并保留 localStorage 作为离线降级/缓存层。
 */

import { logger } from '../utils/logger';

export const GLOBAL_CHAT_BOOK_ID = '00000000-0000-0000-0000-000000000000';

interface SessionRow {
  id: string;
  user_id: string;
  session_uuid: string;
  book_id: string;
  mode: string;
  model_name: string | null;
  provider: string;
  title: string | null;
  messages: string;
  activity_snapshot: string | null;
  chapter_id: string | null;
  chapter_title: string | null;
  created_at: string;
  updated_at: string;
  /** 会话级费用与用量（列表接口附加计算） */
  costCny?: number | null;
  promptTokens?: number;
  completionTokens?: number;
  totalTokens?: number;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function handleSessionExpired() {
  document.cookie = 'sw_session=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  sessionStorage.removeItem('sw_authenticated');
  window.location.hash = '#/';
}

async function jsonFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options.headers as Record<string, string>) || {}),
  };
  const r = await fetch(path, { ...options, headers, credentials: 'include' });
  if (r.status === 401) {
    handleSessionExpired();
    throw new Error('会话已过期，请重新验证');
  }
  if (!r.ok) {
    const e = await r.json().catch(() => ({ error: r.statusText }));
    logger.warn('ai session api request failed', { path, status: r.status, error: e.error });
    throw new Error(e.error || r.statusText);
  }
  return r.json() as Promise<T>;
}

interface UISession {
  id: string;
  title: string;
  createdAt: number;
  messages: unknown[];
  mode?: string;
  activitySnapshot?: unknown[];
  chapterId?: string;
  chapterTitle?: string;
  bookId?: string;
  costCny?: number | null;
  totalTokens?: number;
}

export async function fetchSessions(bookId: string, mode: string, fields?: 'meta'): Promise<UISession[]> {
  const params = new URLSearchParams({ mode });
  if (bookId) params.set('bookId', bookId);
  if (fields === 'meta') params.set('fields', 'meta');
  const url = `/api/ai/chat-sessions?${params.toString()}`;
  const rows = await jsonFetch<SessionRow[]>(url);

  return rows.map((row: SessionRow) => ({
    id: row.session_uuid,
    title: row.title || '无标题',
    createdAt: new Date(row.created_at).getTime(),
    messages: (row as any).messages !== undefined ? safeJsonParse((row as any).messages, []) as unknown[] : [],
    mode: row.mode,
    activitySnapshot: row.activity_snapshot ? safeJsonParse(row.activity_snapshot, undefined) : undefined,
    chapterId: row.chapter_id ?? undefined,
    chapterTitle: row.chapter_title ?? undefined,
    bookId: row.book_id || undefined,
    costCny: row.costCny ?? null,
    totalTokens: row.totalTokens ?? 0,
  }));
}

export async function fetchSessionMessages(sessionUuid: string): Promise<unknown[]> {
  const data = await jsonFetch<{ messages: string }>(
    `/api/ai/chat-sessions/${encodeURIComponent(sessionUuid)}/messages`,
  );
  return safeJsonParse(data.messages, []) as unknown[];
}

export async function upsertSession(data: {
  session_uuid: string;
  book_id: string;
  mode: string;
  title?: string;
  messages: unknown[];
  activity_snapshot?: unknown[];
  chapter_id?: string;
  chapter_title?: string;
}): Promise<void> {
  await jsonFetch<{ id: string }>('/api/ai/chat-sessions', {
    method: 'POST',
    body: JSON.stringify({
      session_uuid: data.session_uuid,
      book_id: data.book_id,
      mode: data.mode,
      title: data.title || null,
      messages: JSON.stringify(data.messages),
      activity_snapshot: data.activity_snapshot ? JSON.stringify(data.activity_snapshot) : undefined,
      chapter_id: data.chapter_id ?? undefined,
      chapter_title: data.chapter_title || undefined,
    }),
  });
}

export async function deleteSession(sessionUuid: string): Promise<void> {
  await jsonFetch<{ success: boolean }>(`/api/ai/chat-sessions/${encodeURIComponent(sessionUuid)}`, {
    method: 'DELETE',
  });
}

/** 仅重命名会话（保留消息内容；bookId 用于确认归属作品） */
export async function renameSession(sessionUuid: string, title: string, bookId?: string): Promise<void> {
  await jsonFetch<{ id: string }>('/api/ai/chat-sessions', {
    method: 'POST',
    body: JSON.stringify({
      session_uuid: sessionUuid,
      title,
      book_id: bookId || GLOBAL_CHAT_BOOK_ID,
    }),
  });
}

export async function batchDeleteSessions(uuids: string[]): Promise<void> {
  await jsonFetch<{ success: boolean }>('/api/ai/chat-sessions/batch-delete', {
    method: 'POST',
    body: JSON.stringify({ uuids }),
  });
}

function safeJsonParse<T>(raw: string, fallback: T): T {
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}
